/*
 * By Noth — <bn-autoplay-video>
 *
 * Muted, looping video with no play button, at the best quality the connection can carry:
 *  - 1080p by default;
 *  - 720p only when the browser reports a clearly modest connection (3G-class or under 2 Mbps);
 *  - 480p only on 2G or when the visitor has switched on data-saver;
 *  - nothing is downloaded until the video is within a couple of screens of the viewport
 *    (the still poster shows meanwhile), then it plays only while it is on screen.
 * Honours prefers-reduced-motion by leaving the video paused with native controls instead.
 */
if (!customElements.get('bn-autoplay-video')) {
  class BnAutoplayVideo extends HTMLElement {
    #observer = null;
    #inView = false;
    #attached = false;

    connectedCallback() {
      const video = this.querySelector('video');
      if (!video) return;

      // Autoplay only works if the video is muted and inline.
      video.muted = true;
      video.loop = true;
      video.setAttribute('playsinline', '');

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.#attach(video);
        video.controls = true;
        return;
      }

      // Warm up well before it is visible, play when mostly visible.
      // Hidden copies (e.g. the desktop grid while on mobile) never intersect, so they never load.
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.#attach(video);
            this.#inView = entry.isIntersecting && entry.intersectionRatio >= 0.4;
            if (this.#inView) this.#play(video);
            else video.pause();
          }
        },
        { threshold: [0, 0.4], rootMargin: '1200px 400px' }
      );
      this.#observer.observe(this);
      document.addEventListener('visibilitychange', this.#handleVisibility);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#observer = null;
      document.removeEventListener('visibilitychange', this.#handleVisibility);
    }

    #pick(video) {
      const { sd, md, hd } = video.dataset;
      const connection = navigator.connection || {};
      const type = connection.effectiveType || '';
      if (connection.saveData || /(^|-)2g$/.test(type)) return sd || md || hd;
      if (type === '3g' || (connection.downlink && connection.downlink < 2)) return md || hd || sd;
      return hd || md || sd;
    }

    #attach(video) {
      if (this.#attached) return;
      const url = this.#pick(video);
      if (!url) return;
      this.#attached = true;
      video.preload = 'auto';
      video.src = url;
    }

    #play(video) {
      this.#attach(video);
      video.play()?.catch(() => {});
    }

    #handleVisibility = () => {
      const video = this.querySelector('video');
      if (!video) return;
      if (document.hidden) video.pause();
      else if (this.#inView) this.#play(video);
    };
  }

  customElements.define('bn-autoplay-video', BnAutoplayVideo);
}